import { map, startCase } from "lodash-es";

import { MDSIconKind } from "@/mds";
import type { AgentFilterSelectItem } from "@/pages/agent/components/agent-filter-select";
import {
  AssistantProjectStatus,
  AssistantRoutineStatus,
  AssistantTaskStatus,
} from "@/store/assistant/types";

export const normalizeStatusValue = ({ status }: { status: string }) => status.trim().toLowerCase();

/**
 * Icons for agent entity statuses, drawn from the circle family so statuses
 * read as one set across the overlay's filter menus. Shared string values use
 * the same icon, so a single map covers every supported entity type.
 */
const AGENT_STATUS_ICON_KINDS: Record<string, MDSIconKind> = {
  [AssistantTaskStatus.Pending]: MDSIconKind.Circle,
  [AssistantTaskStatus.InProgress]: MDSIconKind.CircleDot,
  [AssistantTaskStatus.Completed]: MDSIconKind.CheckCircleSolid,
  [AssistantTaskStatus.Canceled]: MDSIconKind.CircleX,
  [AssistantProjectStatus.Active]: MDSIconKind.CircleDot,
  [AssistantProjectStatus.Resolved]: MDSIconKind.CheckCircleSolid,
  [AssistantProjectStatus.Archived]: MDSIconKind.CircleMinus,
  [AssistantRoutineStatus.Paused]: MDSIconKind.CircleMinus,
  [AssistantRoutineStatus.Unreviewed]: MDSIconKind.CircleExclamation,
  [AssistantTaskStatus.Unknown]: MDSIconKind.CircleExclamation,
};

/** Falls back to the neutral open circle for statuses added later. */
export const getAgentStatusIconKind = ({ status }: { status: string }) =>
  AGENT_STATUS_ICON_KINDS[normalizeStatusValue({ status })] ?? MDSIconKind.Circle;

export const formatStatusLabel = ({ status }: { status: string }) => {
  const normalizedStatus = status.trim();

  if (!normalizedStatus) {
    return "Unknown";
  }

  if (normalizeStatusValue({ status }) === AssistantRoutineStatus.Unreviewed) {
    return "Adapting...";
  }

  return startCase(normalizedStatus);
};

export const buildAgentStatusFilterItems = ({
  allStatusesFilter,
  statusFilter,
  statusFilterValues,
}: {
  allStatusesFilter: string;
  statusFilter: string;
  statusFilterValues: string[];
}): AgentFilterSelectItem[] => {
  const statusFilterItems: AgentFilterSelectItem[] = [
    {
      id: allStatusesFilter,
      label: "All statuses",
      iconKind: MDSIconKind.Filter,
    },
  ];
  const hasSelectedStatus =
    statusFilter === allStatusesFilter || statusFilterValues.includes(statusFilter);

  if (!hasSelectedStatus) {
    statusFilterItems.push({
      id: statusFilter,
      label: formatStatusLabel({ status: statusFilter }),
      iconKind: getAgentStatusIconKind({ status: statusFilter }),
    });
  }

  statusFilterItems.push(
    ...map(statusFilterValues, status => ({
      id: status,
      label: formatStatusLabel({ status }),
      iconKind: getAgentStatusIconKind({ status }),
    }))
  );

  return statusFilterItems;
};
